import { compact, isArray, isNull, mergeWith } from 'lodash';
import moment from 'moment';

import {
    CampaignCriteriaKey,
    getCampaignFilterCriteriaContainers,
    StatisticsByAllGoals
} from '../declarations/filter/filter';
import { flattenContainer, getStrictExpressions } from '../service/FilterHelpers/FilterHelpers';
import { GridScreenLinkBuilder } from '../service/GridScreenLinkBuilder';
import { BudgetType, BudgetValue, CampaignStrategyBudgetInfo, DayBudgetValue } from '../typings/budget';
import { campaigns, ClientInfo, groups } from '../typings/common';
import {
    Campaign,
    CampaignBaseInfo,
    CampaignPlatform,
    CampaignsCalcTypeFilter,
    CampaignStatusForFilter,
    CampaignStrategy,
    CampaignType,
    ClientData,
    StatusInfo,
    TimeTargetingSettings,
    TimeTargetingStatus
} from '../typings/domain';
import {
    CampaignFilterStrategyType,
    CampaignsQueryFilter,
    Criteria,
    EnumSetItem,
    FilterExpression,
    FilterExpressionEnumSetValue,
    FilterExpressionValue,
    FilterPartialExpression,
    GoalStatsQueryFilter,
    StatsQueryFilter
} from '../typings/filter';
import {
    adGroupsRowFragmentForEdit_GdContentPromotionAdGroup_campaign_GdContentPromotionCampaign_strategy,
    adGroupsRowFragmentForEdit_GdContentPromotionAdGroup_campaign_GdPriceCampaign_strategy,
    adGroupsRowFragmentForEdit_GdCpmAudioAdGroup_campaign_GdContentPromotionCampaign_strategy,
    adGroupsRowFragmentForEdit_GdCpmAudioAdGroup_campaign_GdPriceCampaign_strategy,
    adGroupsRowFragmentForEdit_GdCpmIndoorAdGroup_campaign_GdContentPromotionCampaign_strategy,
    adGroupsRowFragmentForEdit_GdCpmIndoorAdGroup_campaign_GdPriceCampaign_strategy,
    adGroupsRowFragmentForEdit_GdCpmOutdoorAdGroup_campaign_GdContentPromotionCampaign_strategy,
    adGroupsRowFragmentForEdit_GdCpmOutdoorAdGroup_campaign_GdPriceCampaign_strategy,
    adGroupsRowFragmentForEdit_GdCpmVideoAdGroup_campaign_GdContentPromotionCampaign_strategy,
    adGroupsRowFragmentForEdit_GdCpmVideoAdGroup_campaign_GdPriceCampaign_strategy,
    adGroupsRowFragmentForEdit_GdInternalAdGroup_campaign_GdContentPromotionCampaign_strategy,
    adGroupsRowFragmentForEdit_GdInternalAdGroup_campaign_GdPriceCampaign_strategy,
    adGroupsRowFragmentForEdit_GdSmartAdGroup_campaign_GdContentPromotionCampaign_strategy,
    adGroupsRowFragmentForEdit_GdSmartAdGroup_campaign_GdPriceCampaign_strategy,
    campaignBaseInfoRowFragment,
    campaignFlatStrategyFragment,
    CampaignPayActionInfoQuery,
    CampaignsQuery_client_campaigns_rowset_GdCpmBannerCampaign,
    CampaignsQuery_client_campaigns_rowset_GdCpmBannerCampaign_brandSurveyStatus,
    campaignsRowFragment,
    campaignStatusInfoFragment,
    campaignStrategyBudgetFragment,
    CampaignTruncatedFragment,
    CurrencyCode,
    GdCampaignAction,
    GdCampaignBudgetPeriod,
    GdCampaignBudgetShowMode,
    GdiRecommendationType,
    GdStrategyType,
    GdStrategyTypeInput,
    TimeTargetStatus,
    timeTargetStatusInfoFragment
} from '../typings/graphql-grid';
import { GridSourceParams, PayActionInfo } from '../typings/grid';
import { RecommendationCharacter, RecommendationsIconType } from '../typings/recommendations';
import { ClientFeatures } from '../typings/state';

import {
    getValueFromApiFormat,
    mapAggregatedStatusInfoCounterChild,
    mapBrandSurveyStatusRowToBrandSurveyStatus,
    mapCampaignRowTypeToCampaignType,
    mapStatsDataToStats
} from './common/common';
import { FilterCriteriaMappings, textToCampaignFilterExpression } from './filter/filter';
import { mapRowGoals } from './goal/goal';
import { isRecommendationSupported, mapContextRecommendationFromApiFormat } from './recommendation';

export function mapRowToCampaignTruncatedBaseInfo(
    row: CampaignTruncatedFragment,
    client: ClientData,
    params: GridSourceParams
): CampaignBaseInfo {
    return {
        id: String(row.id),
        isAimingAllowed: row.isAimingAllowed,
        name: row.name,
        brandSurveyId: null,
        brandSurveyStatus: null,
        hasBanners: row.hasAds,
        type: mapCampaignRowTypeToCampaignType(row.type),
        currency: row.currency === null ? CurrencyCode.RUB : row.currency,
        chiefLogin: client.chiefLogin,
        strategy: mapStrategy(row.strategy),
        campaignUrl: GridScreenLinkBuilder.getContextTypeRelevantUrl(
            campaigns,
            groups,
            {
                login: params.login,
                contextIds: [String(row.id)]
            },
            params
        ),
        statusInfo: mapStatusToStatusInfo(row.status),
        access: row.access
    };
}

const getBrandLiftRow = (
    row: campaignBaseInfoRowFragment
): {
    brandSurveyId: string | null;
    brandSurveyStatus: CampaignsQuery_client_campaigns_rowset_GdCpmBannerCampaign_brandSurveyStatus | null;
} => {
    if (row.__typename === 'GdCpmBannerCampaign') {
        const data = row as CampaignsQuery_client_campaigns_rowset_GdCpmBannerCampaign;

        return {
            brandSurveyId: data.brandSurveyId,
            brandSurveyStatus: data.brandSurveyStatus
        };
    }

    return {
        brandSurveyId: null,
        brandSurveyStatus: null
    };
};

export function mapRowToCampaignBaseInfo(
    row: campaignsRowFragment,
    client: ClientData,
    params: GridSourceParams
): CampaignBaseInfo {
    const { brandSurveyId, brandSurveyStatus } = getBrandLiftRow(row);

    return {
        id: String(row.id),
        isAimingAllowed: false,
        name: row.name,
        brandSurveyId,
        hasBanners: row.hasAds,
        brandSurveyStatus: mapBrandSurveyStatusRowToBrandSurveyStatus(brandSurveyStatus),
        type: mapCampaignRowTypeToCampaignType(row.type),
        currency: row.currency === null ? CurrencyCode.RUB : row.currency,
        chiefLogin: client.chiefLogin,
        strategy: mapStrategy(row.strategy),
        campaignUrl: GridScreenLinkBuilder.getContextTypeRelevantUrl(
            campaigns,
            groups,
            {
                login: params.login,
                contextIds: [String(row.id)]
            },
            params
        ),
        statusInfo: mapStatusToStatusInfo(row.status),
        access: row.access
    };
}

export type AdGroupsRowFragmentForEditCampaignStrategy =
    | adGroupsRowFragmentForEdit_GdContentPromotionAdGroup_campaign_GdContentPromotionCampaign_strategy
    | adGroupsRowFragmentForEdit_GdContentPromotionAdGroup_campaign_GdPriceCampaign_strategy
    | adGroupsRowFragmentForEdit_GdCpmAudioAdGroup_campaign_GdContentPromotionCampaign_strategy
    | adGroupsRowFragmentForEdit_GdCpmAudioAdGroup_campaign_GdPriceCampaign_strategy
    | adGroupsRowFragmentForEdit_GdCpmIndoorAdGroup_campaign_GdContentPromotionCampaign_strategy
    | adGroupsRowFragmentForEdit_GdCpmIndoorAdGroup_campaign_GdPriceCampaign_strategy
    | adGroupsRowFragmentForEdit_GdCpmOutdoorAdGroup_campaign_GdContentPromotionCampaign_strategy
    | adGroupsRowFragmentForEdit_GdCpmOutdoorAdGroup_campaign_GdPriceCampaign_strategy
    | adGroupsRowFragmentForEdit_GdCpmVideoAdGroup_campaign_GdContentPromotionCampaign_strategy
    | adGroupsRowFragmentForEdit_GdCpmVideoAdGroup_campaign_GdPriceCampaign_strategy
    | adGroupsRowFragmentForEdit_GdSmartAdGroup_campaign_GdContentPromotionCampaign_strategy
    | adGroupsRowFragmentForEdit_GdSmartAdGroup_campaign_GdPriceCampaign_strategy
    | adGroupsRowFragmentForEdit_GdInternalAdGroup_campaign_GdContentPromotionCampaign_strategy
    | adGroupsRowFragmentForEdit_GdInternalAdGroup_campaign_GdPriceCampaign_strategy;

export function mapStrategyForPreviewBanner(strategy: AdGroupsRowFragmentForEditCampaignStrategy): CampaignStrategy {
    if (strategy.__typename === 'GdCampaignStrategyManual') {
        return {
            type: GdStrategyType.DEFAULT,
            isAutoBudget: strategy.isAutoBudget,
            platform: strategy.platform
        };
    } else if (strategy.strategyType !== GdStrategyType.DEFAULT) {
        return {
            type: strategy.strategyType,
            isAutoBudget: strategy.isAutoBudget,
            platform: strategy.platform
        };
    }
    throw Error('Неподдерживаемый тип стратегии');
}

function mapStrategy(strategy: campaignFlatStrategyFragment): CampaignStrategy {
    if (strategy.__typename === 'GdCampaignStrategyManual') {
        return {
            type: GdStrategyType.DEFAULT,
            isAutoBudget: strategy.isAutoBudget,
            platform: strategy.platform,
            separateBidding: getValueFromApiFormat(strategy.separateBidding)
        };
    } else if (strategy.strategyType !== GdStrategyType.DEFAULT) {
        return {
            type: strategy.strategyType,
            isAutoBudget: strategy.isAutoBudget,
            platform: strategy.platform
        };
    }
    throw Error('Неподдерживаемый тип стратегии');
}

export function mapRowToCampaign(row: campaignsRowFragment, client: ClientData, params: GridSourceParams): Campaign {
    const recommendations = (row.recommendations || [])
        .filter(recommendation => isRecommendationSupported(recommendation.type))
        .map(mapContextRecommendationFromApiFormat);
    const { aggregatedStatusInfo } = row;

    const rowGoalStats = row.goalStats || [];

    return Object.assign(mapRowToCampaignBaseInfo(row, client, params), {
        agencyId: row.agencyId ? String(row.agencyId) : null,
        name: row.name,
        currency: row.currency,
        type: mapCampaignRowTypeToCampaignType(row.type),
        stats: row.stats ? mapStatsDataToStats(row.stats) : {},
        goalStats: mapRowGoals(rowGoalStats),
        restSum: Number(row.sum),
        budget: getCampaignBudgetFromApiFormat(row.strategy.budget),
        needPayment: row.status.needsNewPayment,
        moneyBlocked: row.status.moneyBlocked,
        recommendations: {
            data: recommendations.length !== 0 ? recommendations : undefined,
            recommendationsIconType: recommendations.some(
                recommendation => recommendation.character === RecommendationCharacter.attention
            )
                ? RecommendationsIconType.red
                : RecommendationsIconType.yellow
        },
        aggregatedStatusInfo:
            aggregatedStatusInfo !== null
                ? {
                      status: aggregatedStatusInfo.status,
                      reasons: compact(aggregatedStatusInfo.reasons),
                      counters: {
                          adgroups: mapAggregatedStatusInfoCounterChild(aggregatedStatusInfo.counters.adgroups)
                      }
                  }
                : undefined,
        aggregatedStatus: row.aggregatedStatus,
        startDate: moment(row.startDate),
        endDate: row.endDate ? moment(row.endDate) : undefined
    });
}

export function getCampaignBudgetFromApiFormat(
    budget: campaignStrategyBudgetFragment | null
): CampaignStrategyBudgetInfo {
    let value: BudgetValue | undefined;

    if (budget) {
        switch (budget.period) {
            case GdCampaignBudgetPeriod.DAY:
                value = getDayBudgetValueFromApiFormat(Number(budget.sum), budget.showMode);
                break;
            case GdCampaignBudgetPeriod.WEEK:
                value = {
                    type: BudgetType.week,
                    sum: Number(budget.sum)
                };
                break;
            case GdCampaignBudgetPeriod.CUSTOM:
                value = {
                    type: BudgetType.custom,
                    sum: Number(budget.sum),
                    start: getValueFromApiFormat(budget.start),
                    finish: getValueFromApiFormat(budget.finish)
                };
                break;
            default:
                break;
        }
    }

    return {
        type: budget ? budget.period : null,
        value
    };
}

function getDayBudgetValueFromApiFormat(apiSumValue: number, apiModeValue: GdCampaignBudgetShowMode): DayBudgetValue {
    return {
        type: BudgetType.day,
        isActive: apiSumValue !== 0,
        sum: apiSumValue,
        mode: isNull(apiModeValue) ? GdCampaignBudgetShowMode.DEFAULT : apiModeValue
    };
}

/**
 * Формирует данные с информацией о статусе кампании на основе данных от сервера
 */
export function mapStatusToStatusInfo(status: campaignStatusInfoFragment): StatusInfo {
    const { primaryStatus, timeTargetStatus, primaryStatusDesc } = status;

    const result: StatusInfo = {
        type: primaryStatus,
        hintType: primaryStatusDesc === null ? undefined : primaryStatusDesc
    };

    result.timeTargetingSettings = getTimeTargetingSettingsFromApiFormat(timeTargetStatus);

    return result;
}

/**
 * Формирует данные о настройке таймтагретинга на основе данных от сервера
 */
export function getTimeTargetingSettingsFromApiFormat(
    timeTargetingStatusInfo: timeTargetStatusInfoFragment
): TimeTargetingSettings | undefined {
    const { status, activationTime } = timeTargetingStatusInfo;

    if (status === null || activationTime === null) {
        return;
    }

    const startTime = moment.parseZone(activationTime);

    return {
        startTime,
        status: getTimeTargetingStatusFromApiFormat(status)
    };
}

/**
 * Преобразует серверный статус таймтаргетинга в клиентский формат
 */
export function getTimeTargetingStatusFromApiFormat(apiStatus: TimeTargetStatus): TimeTargetingStatus {
    const dictionary = {
        [TimeTargetStatus.ACTIVE]: TimeTargetingStatus.active,
        [TimeTargetStatus.TODAY]: TimeTargetingStatus.today,
        [TimeTargetStatus.TOMORROW]: TimeTargetingStatus.tomorrow,
        [TimeTargetStatus.THIS_WEEK]: TimeTargetingStatus.thisWeek,
        [TimeTargetStatus.LATER_THAN_WEEK]: TimeTargetingStatus.laterThanWeek
    };

    if (!dictionary[apiStatus]) {
        throw new Error(`Статус ${apiStatus} не поддерживается`);
    }

    return dictionary[apiStatus];
}

const mappings = new FilterCriteriaMappings<CampaignsQueryFilter & StatsQueryFilter & GoalStatsQueryFilter>();
const { toBoolean, toDate, toNumber, toEnum } = mappings.valueMappings;

const campaignCriteriaMappings: {
    [key in CampaignCriteriaKey]: (expression: FilterExpression) => CampaignsQueryFilter;
} = {
    id: mappings.includes('campaignIdIn', 'campaignIdNotIn'),
    types: mappings.equality('typeIn', toEnum(CampaignType)),
    name: mappings.contains('nameContains', 'nameNotContains'),
    status: mappings.equality('statusIn', toEnum(CampaignStatusForFilter)),
    // hasDailyBudgetLimit: mappings.boolean('hasDailyBudget', toBoolean),
    startDate: mappings.comparison('minStartDate', 'maxStartDate', toDate, false),
    endDate: mappings.comparison('minFinishDate', 'maxFinishDate', toDate, false),
    platform: mappings.equality('platformIn', toEnum(CampaignPlatform)),
    recommendations: mappings.equality('recommendations', toEnum(GdiRecommendationType)),
    strategy: mappings.equality('campaignStrategyTypeIn', toEnum(GdStrategyTypeInput)),
    hasBidModifiers: mappings.boolean('hasBidModifiers', toBoolean),
    hasBroadmatch: mappings.boolean('hasBroadmatch', toBoolean),
    hasMetrika: mappings.boolean('hasMetrika', toBoolean),
    hasSiteMonitoring: mappings.boolean('hasSiteMonitoring', toBoolean),
    cost: mappings.comparison('minCost', 'maxCost', toNumber),
    revenue: mappings.comparison('minRevenue', 'maxRevenue', toNumber),
    profitability: mappings.comparison('minProfitability', 'maxProfitability', toNumber),
    avgGoalCost: (expression: FilterExpression): CampaignsQueryFilter => {
        if (expression.additionalCriteriaKey && expression.additionalCriteriaKey !== StatisticsByAllGoals) {
            return mappings.comparison(
                'minCostPerAction',
                'maxCostPerAction',
                toNumber,
                false,
                expression.additionalCriteriaKey
            )(expression);
        }

        return mappings.comparison('minAvgGoalCost', 'maxAvgGoalCost', toNumber)(expression);
    },
    avgClickCost: mappings.comparison('minAvgClickCost', 'maxAvgClickCost', toNumber),
    avgCpmCost: mappings.comparison('minCpmPrice', 'maxCpmPrice', toNumber),
    shows: mappings.comparison('minShows', 'maxShows', toNumber),
    clicks: mappings.comparison('minClicks', 'maxClicks', toNumber),
    ctr: mappings.comparison('minCtr', 'maxCtr', toNumber),
    goals: (expression: FilterExpression): CampaignsQueryFilter => {
        if (expression.additionalCriteriaKey && expression.additionalCriteriaKey !== StatisticsByAllGoals) {
            return mappings.comparison('minGoals', 'maxGoals', toNumber, false, expression.additionalCriteriaKey)(
                expression
            );
        }

        return mappings.comparison('minGoals', 'maxGoals', toNumber)(expression);
    },
    conversionRate: (expression: FilterExpression): CampaignsQueryFilter => {
        if (expression.additionalCriteriaKey && expression.additionalCriteriaKey !== StatisticsByAllGoals) {
            return mappings.comparison(
                'minConversionRate',
                'maxConversionRate',
                toNumber,
                false,
                expression.additionalCriteriaKey
            )(expression);
        }

        return mappings.comparison('minConversionRate', 'maxConversionRate', toNumber)(expression);
    },
    bounceRate: mappings.comparison('minBounceRate', 'maxBounceRate', toNumber)
};

function appendSameNameToStrategiesList(v: FilterExpressionValue, features: ClientFeatures): FilterExpressionValue {
    const enumSet = v.value as FilterExpressionEnumSetValue;

    [
        {
            name: CampaignFilterStrategyType.DEFAULT,
            doubleName: GdStrategyType.CPM_DEFAULT
        },
        features.isInAppEventsInRmpEnabled && {
            name: CampaignFilterStrategyType.OPTIMIZE_CONVERSIONS,
            doubleName: GdStrategyType.OPTIMIZE_INSTALLS
        },
        {
            name: CampaignFilterStrategyType.CPM_MAX_REACH,
            doubleName: GdStrategyType.CPM_MAX_REACH_CUSTOM_PERIOD
        },
        {
            name: CampaignFilterStrategyType.CPM_MAX_IMPRESSIONS,
            doubleName: GdStrategyType.CPM_MAX_IMPRESSIONS_CUSTOM_PERIOD
        }
    ].forEach(pair => {
        if (
            pair &&
            enumSet.find((value: EnumSetItem) => {
                return value.key === pair.name;
            })
        ) {
            enumSet.push({ key: pair.doubleName, title: '' });
        }
    });

    return v;
}

const expressionToCampaignsQueryFilter = (
    expression: FilterExpression,
    features: ClientFeatures
): CampaignsQueryFilter => {
    // дописываем к ручным стратегиям (DEFAULT)
    // ручные стратегии для медийки (CPM_DEFAULT)
    if (expression.criteriaKey === CampaignCriteriaKey.strategy) {
        expression.value = appendSameNameToStrategiesList(expression.value, features);
    }

    return campaignCriteriaMappings[expression.criteriaKey](expression);
};

export const mapCampaignFilterToApiFormat = (
    expressions: FilterPartialExpression[],
    clientInfo: ClientInfo,
    features: ClientFeatures,
    calcType?: CampaignsCalcTypeFilter
): CampaignsQueryFilter => {
    const criteria: Criteria[] = [];

    getCampaignFilterCriteriaContainers(clientInfo, features, calcType === CampaignsCalcTypeFilter.CPM).forEach(
        ({ criteria: c }) => criteria.push(...flattenContainer(c))
    );

    return getStrictExpressions(expressions, criteria, textToCampaignFilterExpression)
        .map(filterExpression => expressionToCampaignsQueryFilter(filterExpression, features))
        .reduce(
            (obj, otherObj) =>
                mergeWith(obj, otherObj, (objValue, srcValue) => {
                    if (isArray(objValue)) {
                        return objValue.concat(srcValue);
                    }

                    return undefined;
                }),
            {}
        );
};

export const isCampaignEditable = (campaign: CampaignBaseInfo): boolean => {
    return campaign && campaign.access.canEdit;
};

export function mapCampaignTypesQueryResponseToCampaignTypes(data: CampaignPayActionInfoQuery): PayActionInfo {
    return {
        campaignTypes: data.client.campaigns.features.campaignTypes,
        unsupportedCampaigns: data.client.campaigns.rowset
            .filter(el => !el.access.actions.includes(GdCampaignAction.PAY_BY_CASH))
            .map(el => ({
                id: String(el.id),
                name: el.name
            }))
    };
}
